import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { ResultsPitch } from "./fpl/ResultsPitch";
import { Trophy, Sparkles, RotateCcw, X, Home, Plane } from "lucide-react";

const CONFETTI_COLORS = ["#10B981", "#38BDF8", "#ffffff", "#16d878", "#e74c3c"];
const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden z-20">
    {Array.from({ length: 40 }).map((_, i) => {
      const size = 6 + Math.random() * 6;
      return (
        <span
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${Math.random() * 0.7}s`,
            animationDuration: `${1.6 + Math.random() * 1.3}s`,
          }}
        />
      );
    })}
  </div>
);

const SeasonResult = ({ open, result, xi = [], onClose, onReset, ctaLabel = "Build a new XI", ctaIcon = true }) => {
  const [shown, setShown] = useState(0); // how many fixtures have been "played out"
  const [done, setDone] = useState(false);
  const listRef = useRef(null);
  const timerRef = useRef(null);

  // Animate the season game-by-game when the modal opens.
  useEffect(() => {
    clearInterval(timerRef.current);
    if (open && result) {
      setShown(0);
      setDone(false);
      timerRef.current = setInterval(() => {
        setShown((s) => {
          const next = s + 1;
          if (next >= result.fixtures.length) {
            clearInterval(timerRef.current);
            setDone(true);
            return result.fixtures.length;
          }
          return next;
        });
      }, 130);
    }
    return () => clearInterval(timerRef.current);
  }, [open, result]);

  // Auto-scroll the fixtures list as games are played.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [shown]);

  if (!result) return null;

  const played = result.fixtures.slice(0, shown);
  const runW = played.filter((f) => f.res === "W").length;
  const runD = played.filter((f) => f.res === "D").length;
  const runL = played.filter((f) => f.res === "L").length;
  const runPts = runW * 3 + runD;

  const skip = () => {
    clearInterval(timerRef.current);
    setShown(result.fixtures.length);
    setDone(true);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#1E293B] border border-white/10 text-white max-w-md p-0 overflow-x-hidden overflow-y-auto max-h-[92vh]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {done && <Confetti />}
        {/* running score header */}
        <div className={`relative px-6 pt-8 pb-5 text-center ${result.perfect && done ? "hero-glow" : ""}`}>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold label-eyebrow text-[#10B981]">
            {done ? (
              result.perfect ? <Sparkles className="w-4 h-4" /> : <Trophy className="w-4 h-4" />
            ) : (
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
            )}
            {done ? "Season Complete" : `Simulating\u2026 Game ${shown}/38`}
          </div>

          <div className={`font-display font-extrabold text-white leading-none mt-4 ${done ? "pop-in" : ""}`}>
            <span className={done ? "text-6xl text-[#10B981]" : "text-5xl"}>{done ? result.points : runPts}</span>
            <span className="text-lg text-muted-38 font-semibold ml-1.5">pts</span>
          </div>
          <div className="text-muted-38 text-sm mt-2">
            {done ? `${result.w}W · ${result.d}D · ${result.l}L` : `${runW}W · ${runD}D · ${runL}L`}
          </div>

          {done && <p className="text-white text-[15px] font-medium mt-4 leading-relaxed">{result.verdict}</p>}
        </div>

        {/* fixtures list */}
        <div className="border-t border-white/5">
          <div className="px-5 pt-3 pb-2 text-muted-38 text-[11px] font-semibold label-eyebrow">
            All 38 games
          </div>
          <div ref={listRef} className="max-h-[260px] overflow-y-auto px-3 pb-3 space-y-1">
            {played.map((f) => (
              <div
                key={f.mw}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-white/[0.02]"
              >
                <span className="text-muted-38 text-[10px] w-5 shrink-0">{f.mw}</span>
                {f.home ? (
                  <Home className="w-3 h-3 text-slate-500 shrink-0" />
                ) : (
                  <Plane className="w-3 h-3 text-slate-500 shrink-0" />
                )}
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center text-[8px] font-bold text-white shrink-0"
                  style={{ backgroundColor: f.color }}
                >
                  {f.short}
                </span>
                <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">{f.opp}</span>
                <span className="font-display font-bold text-white text-sm tabular-nums">
                  {f.gf}–{f.ga}
                </span>
                <span
                  className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    f.res === "W"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : f.res === "D"
                      ? "bg-white/10 text-slate-300"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {f.res}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* totals + actions */}
        {done ? (
          <>
            <div className="px-4 pb-4">
              <ResultsPitch xi={xi} total={result.points} />
            </div>
            <div className="grid grid-cols-4 gap-px bg-white/5 border-y border-white/5">
              {[
                { v: result.points, l: "Points" },
                { v: `${result.position}${ordinal(result.position)}`, l: "Position" },
                { v: result.gd > 0 ? `+${result.gd}` : result.gd, l: "Goal Diff" },
                { v: result.avg, l: "Avg Rating" },
              ].map((s) => (
                <div key={s.l} className="bg-[#1E293B] py-4 text-center">
                  <div className="font-display font-bold text-lg text-white">{s.v}</div>
                  <div className="text-muted-38 text-[10px] mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="px-6 py-5">
              <button
                onClick={onReset}
                className="btn-green w-full rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2"
              >
                {ctaIcon && <RotateCcw className="w-4 h-4" />} {ctaLabel}
              </button>
            </div>
          </>
        ) : (
          <div className="px-6 py-4 border-t border-white/5">
            <button
              onClick={skip}
              className="btn-ghost w-full rounded-xl py-2.5 text-sm font-medium text-slate-300"
            >
              Skip to final result
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default SeasonResult;
